/**
 * Token Estimation Utilities
 *
 * Provides rough estimates of token usage for OpenAI API calls.
 * Used for context length warnings and file size validation.
 *
 * Estimation logic:
 * - Text: ~4 characters per token (OpenAI average)
 * - Images: ~765 tokens for high detail, ~85 for low detail
 * - Documents: Variable based on extracted text length
 */

import type { DraftAttachment } from '@ctrl-enter/types';
import type { ChatMessage } from '@ctrl-enter/types/chat-history';

/**
 * Context usage status
 */
export type ContextUsageStatus = 'safe' | 'warning' | 'critical' | 'exceeded';

/**
 * Context usage information
 */
export type ContextUsage = {
  percentage: number;
  status: ContextUsageStatus;
  tokensRemaining: number;
};

// OpenAI context limits
export const OPENAI_LIMITS = {
  GPT4_VISION_CONTEXT: 128000, // GPT-4 Vision model context window
  WARNING_THRESHOLD: 0.8, // Warn at 80% capacity (102,400 tokens)
  CRITICAL_THRESHOLD: 0.95, // Critical at 95% capacity (121,600 tokens)
  FILE_MAX_TOKENS: 100000, // Maximum tokens for a file OpenAI can process
} as const;

// Average token estimates
export const TOKEN_ESTIMATES = {
  CHARS_PER_TOKEN: 4, // Average for English text
  IMAGE_HIGH_DETAIL: 765, // High detail image
  IMAGE_LOW_DETAIL: 85, // Low detail image
  SCREENSHOT: 765, // Screenshots use high detail
  PDF_PAGE: 500, // Average tokens per PDF page
  CSV_MB: 262000, // Tokens per MB for CSV (extracted text is ~same size as file after conversion)
  EXCEL_MB: 250000, // Tokens per MB for Excel (similar to CSV)
  TEXT_MB: 262000, // Tokens per MB for plain text (4 chars/token)
} as const;

/**
 * Estimate tokens from text content
 */
export function estimateTextTokens(text: string): number {
  if (!text) return 0;
  return Math.ceil(text.length / TOKEN_ESTIMATES.CHARS_PER_TOKEN);
}

/**
 * Estimate tokens for an image attachment
 */
export function estimateImageTokens(attachment: DraftAttachment): number {
  // Screenshots use high detail for better analysis
  if (attachment.isScreenshot) {
    return TOKEN_ESTIMATES.SCREENSHOT;
  }

  // Regular uploads use low detail by default
  return TOKEN_ESTIMATES.IMAGE_LOW_DETAIL;
}

/**
 * Estimate tokens for a document attachment
 * Uses file size and MIME type to estimate
 */
export function estimateDocumentTokens(fileSizeMB: number, mimeType: string): number {
  switch (mimeType) {
    case 'text/csv':
      return Math.ceil(fileSizeMB * TOKEN_ESTIMATES.CSV_MB);

    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': // .xlsx
    case 'application/vnd.ms-excel': // .xls
      return Math.ceil(fileSizeMB * TOKEN_ESTIMATES.EXCEL_MB);

    case 'text/plain':
    case 'text/markdown':
      return Math.ceil(fileSizeMB * TOKEN_ESTIMATES.TEXT_MB);

    case 'application/pdf':
      // Rough estimate: 1MB PDF ≈ 10 pages ≈ 5000 tokens
      return Math.ceil(fileSizeMB * 5000);

    default:
      // Conservative estimate for unknown types
      return Math.ceil(fileSizeMB * 10000);
  }
}

/**
 * Estimate tokens for any attachment
 */
export function estimateAttachmentTokens(attachment: DraftAttachment): number {
  // If we have a pre-calculated estimate from backend, use it
  if (attachment.estimatedTokens) {
    return attachment.estimatedTokens;
  }

  // Use contentType (standard field in DraftAttachment)
  const contentType = attachment.contentType || '';

  // Image attachments
  const isImage = contentType.startsWith('image/') || attachment.isScreenshot;
  if (isImage) {
    return estimateImageTokens(attachment);
  }

  // Document attachments - estimate from file size
  if (attachment.file) {
    const fileSizeMB = attachment.file.size / (1024 * 1024);
    return estimateDocumentTokens(fileSizeMB, contentType);
  }

  // Fallback: conservative estimate
  return 5000;
}

/**
 * Estimate total tokens for a message with attachments
 */
export function estimateMessageTokens(messageText: string, attachments: DraftAttachment[]): number {
  const textTokens = estimateTextTokens(messageText);
  const attachmentTokens = attachments.reduce(
    (sum, attachment) => sum + estimateAttachmentTokens(attachment),
    0
  );

  return textTokens + attachmentTokens;
}

/**
 * Estimate tokens for entire chat history
 */
export function estimateChatTokens(
  messages: Array<{ content: string; role: string }>,
  currentDraft: string,
  currentAttachments: DraftAttachment[]
): number {
  // Estimate historical messages (rough average)
  // Each message pair (user + assistant) ≈ 500 tokens average
  const historyTokens = messages.length * 250;

  // Current draft
  const draftTokens = estimateMessageTokens(currentDraft, currentAttachments);

  return historyTokens + draftTokens;
}

/**
 * Get context usage percentage
 */
export function getContextUsage(estimatedTokens: number): ContextUsage {
  const percentage = estimatedTokens / OPENAI_LIMITS.GPT4_VISION_CONTEXT;
  const tokensRemaining = OPENAI_LIMITS.GPT4_VISION_CONTEXT - estimatedTokens;

  let status: 'safe' | 'warning' | 'critical' | 'exceeded';
  if (percentage >= 1) {
    status = 'exceeded';
  } else if (percentage >= OPENAI_LIMITS.CRITICAL_THRESHOLD) {
    status = 'critical';
  } else if (percentage >= OPENAI_LIMITS.WARNING_THRESHOLD) {
    status = 'warning';
  } else {
    status = 'safe';
  }

  return {
    percentage,
    status,
    tokensRemaining,
  };
}

/**
 * Estimate tokens for a chat history message (includes attachments)
 */
export function estimateChatMessageTokens(message: ChatMessage): number {
  let tokens = estimateTextTokens(message.content);

  // Add attachment tokens from history
  if (message.attached_files && message.attached_files.length > 0) {
    message.attached_files.forEach(file => {
      if (typeof file === 'string') {
        // Legacy format: just S3 key string, use conservative average estimate
        tokens += 2900;
      } else {
        // New format: AttachmentMetadata object with estimated_tokens
        if (file.estimated_tokens) {
          // Use stored token count (accurate!)
          tokens += file.estimated_tokens;
        } else {
          // Fallback: no token estimate stored, use conservative average
          tokens += 2900;
        }
      }
    });
  }

  return tokens;
}

/**
 * Estimate tokens for entire chat history
 */
export function estimateChatHistoryTokens(chatHistory: ChatMessage[]): number {
  return chatHistory.reduce((sum, message) => sum + estimateChatMessageTokens(message), 0);
}

/**
 * Estimate tokens for draft attachments (screenshots + files)
 */
export function estimateDraftAttachmentsTokens(attachments: DraftAttachment[]): number {
  return attachments.reduce((sum, attachment) => sum + estimateAttachmentTokens(attachment), 0);
}

/**
 * Estimate total conversation tokens (draft + history)
 * This is the main function used by the chat view
 */
export function estimateTotalConversationTokens(
  draftMessage: string,
  draftAttachments: DraftAttachment[],
  chatHistory: ChatMessage[]
): number {
  const messageTokens = estimateTextTokens(draftMessage);
  const attachmentsTokens = estimateDraftAttachmentsTokens(draftAttachments);
  const historyTokens = estimateChatHistoryTokens(chatHistory);

  return messageTokens + attachmentsTokens + historyTokens;
}

/**
 * Format token count for display
 */
export function formatTokenCount(tokens: number | undefined | null): string {
  // Handle undefined, null, or NaN values
  if (tokens == null || isNaN(tokens)) {
    return '0';
  }

  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
}

/**
 * Get user-friendly warning message based on context usage
 */
export function getContextWarningMessage(
  status: 'safe' | 'warning' | 'critical' | 'exceeded'
): string | null {
  switch (status) {
    case 'exceeded':
      return 'Context limit exceeded. Start a new chat to continue.';
    case 'critical':
      return 'Chat is almost at capacity. Consider starting a new chat soon.';
    case 'warning':
      return 'Chat is getting long. You may want to start fresh soon.';
    case 'safe':
      return null;
  }
}
